import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../api/client';
import { fetchWalletSnapshot, formatMinor, type WalletSnapshot } from '../api/payments';
import { useAuth } from '../auth/AuthContext';
import { useAuthDialog } from '../auth/AuthDialogContext';
import { BalanceMenu } from './BalanceMenu';
import { BrandMark } from './BrandMark';
import { LanguageSelector } from './LanguageSelector';
import { LuckyWheelNavButton } from './lucky-wheel/LuckyWheelNavButton';

interface HeaderProps {
  onOpenWheel?: () => void;
}

export function Header({ onOpenWheel }: HeaderProps) {
  const { t, i18n } = useTranslation(['common', 'nav', 'payments', 'auth']);
  const { user } = useAuth();
  const { openAuth } = useAuthDialog();
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [unread, setUnread] = useState(0);
  const [balanceOpen, setBalanceOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchWalletSnapshot()
      .then((snap) => {
        if (!cancelled) setWallet(snap.authenticated ? snap : null);
      })
      .catch(() => {
        if (!cancelled) setWallet(null);
      });
    if (user) {
      void apiGet<{ unread: number }>('/api/v1/notifications/unread-count')
        .then((data) => {
          if (!cancelled) setUnread(data.unread);
        })
        .catch(() => {
          if (!cancelled) setUnread(0);
        });
    } else {
      setUnread(0);
    }
    return () => {
      cancelled = true;
    };
  }, [user?.public_id]);

  const playCurrency = wallet?.play_currency || 'AZN';
  const playBalance = wallet?.play_balance_real_minor ?? wallet?.balance_real_minor ?? null;
  const playFlags: Record<string, string> = { AZN: '🇦🇿', RUB: '🇷🇺', EUR: '🇪🇺', USD: '🇺🇸' };

  return (
    <header className={user ? 'site-header site-header-wallet' : 'site-header'}>
      <div className="header-left">
        <NavLink to="/" className="logo" aria-label={t('common:brand')}>
          <BrandMark size={28} />
          <span className="logo-text">
            VBB<span>PLAY</span>
          </span>
        </NavLink>
        {onOpenWheel ? (
          <div className="header-lucky-wheel">
            <LuckyWheelNavButton onOpen={onOpenWheel} compact />
          </div>
        ) : null}
      </div>

      <nav className="desktop-nav" aria-label="Primary">
        <NavLink to="/" end>
          {t('nav:home')}
        </NavLink>
        <NavLink to="/games">{t('nav:casino')}</NavLink>
        <NavLink to="/sports">{t('nav:sports')}</NavLink>
        <NavLink to="/bonuses">{t('nav:bonuses')}</NavLink>
        <NavLink to="/cashier">{t('nav:wallet')}</NavLink>
        <NavLink to="/profile">{t('nav:profile')}</NavLink>
      </nav>

      <div className="header-actions">
        {user ? (
          <>
            <button
              type="button"
              className="balance-pill"
              title={t('payments:balanceTitle')}
              onClick={() => setBalanceOpen(true)}
            >
              <span className="balance-currency-label">
                <span className="balance-flag" aria-hidden="true">{playFlags[playCurrency] || ''}</span>
                {playCurrency}
                <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
                  <path d="m2 4 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <strong>{formatMinor(playBalance, playCurrency, i18n.language || 'az')}</strong>
            </button>
            <Link to="/cashier?tab=deposit" className="btn btn-primary btn-compact">
              {t('payments:topUp')}
            </Link>
            <Link to="/profile" className="avatar-btn" aria-label={t('nav:profile')}>
              <span className="avatar-initial" aria-hidden="true">
                {(user.display_name || user.email || 'V').slice(0, 1).toUpperCase()}
              </span>
              <img className="header-profile-mascot" src="/vbb-fox-avatar.png" alt="" />
            </Link>
            <Link to="/notifications" className="icon-btn" aria-label={t('common:notificationsTitle', { defaultValue: 'Notifications' })}>
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3a6 6 0 016 6v3.5l1.5 2.5H4.5L6 12.5V9a6 6 0 016-6z" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M10 19a2 2 0 004 0" stroke="currentColor" strokeWidth="2" />
              </svg>
              {unread > 0 ? <span className="notify-dot">{unread > 9 ? '9+' : unread}</span> : null}
            </Link>
            <BalanceMenu
              open={balanceOpen}
              onClose={() => setBalanceOpen(false)}
              onUpdated={(snap) => setWallet(snap)}
            />
          </>
        ) : (
          <div className="auth-header-pair">
            <Link
              to="/auth/login"
              className="btn btn-ghost btn-compact auth-login-btn"
              onClick={(event) => {
                event.preventDefault();
                openAuth('login');
              }}
            >
              {t('nav:login')}
            </Link>
            <Link
              to="/auth/register"
              className="btn btn-primary btn-compact auth-register-btn"
              onClick={(event) => {
                event.preventDefault();
                openAuth('register');
              }}
            >
              {t('auth:registerShort')}
            </Link>
          </div>
        )}
        <LanguageSelector />
      </div>
    </header>
  );
}
